import React, { useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import log from "loglevel";
import { Link } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import ReactTooltip from "react-tooltip";
// @ts-ignore
import BootstrapTable from "react-bootstrap-table-next";
// @ts-ignore
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
// @ts-ignore
import paginationFactory from "react-bootstrap-table2-paginator";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addTeamAction,
  modifyTeamAction,
  deleteTeamAction,
} from "store/actions/actionCreators";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import {
  validAccountName,
  validTeamName,
  isStringValid,
} from "util/formValidators";
import {
  Team,
  Account,
  CreateTeamRequestType,
  ModifyTeamRequestType,
  DeleteTeamRequestType,
} from "types";
import { generateAccountDropdownItems } from "util/dashboardUtil";
import { options } from "util/reactTableFuncs";
import TeamsTable from "./TeamsTable";
import genStyles from "styles/General.module.scss";
import styles from "components/dashboard/Dashboard.module.scss";
import * as r from "routes";
import { Form } from "@avlino-inc/alenza-react";

type CreateTeamFormType = {
  name: string | undefined;
  account_id: number | undefined;
  description: string | undefined;
};

type ModifyTeamFormType = {
  name: string | undefined;
  description: string | undefined;
};

const TeamsCard = ({
  teams,
  accounts,
  userIsAdmin,
  userIsAccountOwner,
  showCreateModal,
  setShowCreateModal,
  showSearch,
}: {
  teams: Team[];
  accounts: Account[];
  userIsAdmin: boolean;
  userIsAccountOwner: boolean;
  showCreateModal: boolean;
  setShowCreateModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSearch: boolean;
}) => {
  const dispatch = useDispatch();

  const [selectedTeam, setSelectedTeam] = useState<Team>();
  const [createTeam, setCreateTeam] = useState<CreateTeamFormType>({
    name: undefined,
    description: undefined,
    account_id: undefined,
  });
  const [modifyTeam, setModifyTeam] = useState<ModifyTeamFormType>({
    name: undefined,
    description: undefined,
  });
  const [deleteTeamConfirm, setDeleteTeamConfirm] = useState<string>();
  const [isCreatingTeam, setIsCreatingTeam] = useState(false);
  const [isModifyingTeam, setIsModifyingTeam] = useState(false);
  const [isDeletingTeam, setIsDeletingTeam] = useState(false);
  //   const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const onHandleCreateTeamClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to CREATE Team...");
    if (
      createTeam?.name &&
      createTeam?.account_id &&
      createTeam?.description &&
      validTeamName(createTeam?.name)
    ) {
      const request: CreateTeamRequestType = {
        body: {
          name: createTeam.name,
          description: createTeam.description,
          account_id: createTeam.account_id,
        },
      };
      log.info("-- Dispatching addTeamAction()...");
      dispatch(addTeamAction(request));
      setIsCreatingTeam(false);
      setShowCreateModal(false);
      setCreateTeam({
        name: undefined,
        description: undefined,
        account_id: undefined,
      }); // clear
    } else {
      log.info("One or more fields NOT VALID!");
      log.info("CreateTeamFormType: " + JSON.stringify(createTeam));
    }
  };

  const onHandleModifyTeamClick = (event: any) => {
    event.preventDefault();
    log.info("Attempting to MODIFY Analysis...");
    if (
      selectedTeam &&
      selectedTeam.id &&
      modifyTeam?.name &&
      modifyTeam?.description &&
      validTeamName(modifyTeam?.name)
    ) {
      log.info("All fields valid! Modifying...");
      const request: ModifyTeamRequestType = {
        id: selectedTeam.id,
        body: {
          name: modifyTeam?.name,
          description: modifyTeam?.description,
        },
      };
      log.info("-- Dispatching modifyTeamAction()...");
      dispatch(modifyTeamAction(request));
      setIsModifyingTeam(false);
      setShowSettingsModal(false);
      setSelectedTeam(undefined); // clear
      setModifyTeam({ name: undefined, description: undefined }); // clear
    } else {
      log.info("One or more fields NOT VALID!");
    }
  };

  const confirmTeamDelete = () => {
    return selectedTeam && selectedTeam.name === deleteTeamConfirm;
  };

  const onHandleDeleteTeamClick = (event: any) => {
    event.preventDefault();
    if (selectedTeam && confirmTeamDelete()) {
      const request: DeleteTeamRequestType = {
        id: selectedTeam.id,
      };
      log.info("-- Dispatching deleteTeamAction()...");
      dispatch(deleteTeamAction(request));
      setIsDeletingTeam(false);
      setShowSettingsModal(false);
      setSelectedTeam(undefined); // clear
      setDeleteTeamConfirm(""); //clear
    }
  };

  const onSelectAccountFromDropdown = (accountItem: any) => {
    if (accountItem) {
      log.info(
        "Create Analysis Account Dropdown Item Selected! : " +
          JSON.stringify(accountItem)
      );
      setCreateTeam({
        name: createTeam?.name,
        account_id: accountItem.id,
        description: createTeam?.description,
      });
    }
  };

  const teamsRowEvents = {
    onClick: (e: any, row: Team, rowIndex: number) => {
      log.info("Row Index: " + rowIndex);
      log.info("Row: " + JSON.stringify(row));
      setSelectedTeam(row);
      setModifyTeam({
        name: row.name,
        description: row.description,
      });
      setShowSettingsModal(true);
    },
  };

  const teamsCard = () => {
    return (
      <>
        {teams && accounts ? (
          <TeamsTable
            teams={teams}
            rowEvents={teamsRowEvents}
            showSearch={showSearch}
            showAccount={true}
          />
        ) : null}
      </>
    );
  };

  const [wasNameTouched, setWasNameTouched] = useState(false);
  const [wasDescriptionTouched, setWasDescriptionTouched] = useState(false);

  const inputNameValidity = useMemo(() => {
    const isValid = isStringValid(createTeam?.name, validTeamName);
    const isInvalid = wasNameTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasNameTouched, createTeam?.name]);

  const inputDescriptionValidity = useMemo(() => {
    const isValid = (createTeam?.description?.length || 0) > 0;
    const isInvalid = wasDescriptionTouched && !isValid;
    if (isValid) return "valid";
    else if (isInvalid) return "invalid";
    else return undefined;
  }, [wasDescriptionTouched, createTeam?.description]);

  const selectAccountOptions = useMemo(
    () => generateAccountDropdownItems(accounts),
    [accounts]
  );
  const selectedAccountOption = useMemo(
    () =>
      selectAccountOptions?.find(
        (option) => option.id === createTeam?.account_id
      ),
    [selectAccountOptions, createTeam?.account_id]
  );

  const isFormValid = useMemo(
    () =>
      [inputNameValidity, inputDescriptionValidity].every(
        (value) => value === "valid"
      ) && selectedAccountOption !== undefined,
    [inputNameValidity, inputDescriptionValidity, selectedAccountOption]
  );

  const getValidity = () => {
    if (
      isStringValid(modifyTeam?.name, validTeamName) === true &&
      modifyTeam?.name !== selectedTeam?.name
    ) {
      return "valid";
    } else if (
      isStringValid(modifyTeam?.name, validTeamName) === false ||
      modifyTeam?.name === selectedTeam?.name
    ) {
      return "invalid";
    }
  };

  const createTeamModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showCreateModal}
          onHide={() => {
            setShowCreateModal(false);
            setCreateTeam({
              name: undefined,
              description: undefined,
              account_id: undefined,
            });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Create Team <FontAwesomeIcon icon={["fal", "users"]} />
            </Modal.Title>
          </Modal.Header>
          <form name="create-team-form">
            <Modal.Body>
              <div className="w-100" style={{ textAlign: "center" }}>
                <Form.Group controlId="team_name_create">
                  <Form.Input
                    type="text"
                    placeholder="Team Name"
                    value={createTeam?.name || ""}
                    status={inputNameValidity}
                    onBlur={() => setWasNameTouched(true)}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLInputElement) {
                        const value = e.target.value;
                        setCreateTeam((state) => ({
                          ...state,
                          name: value,
                        }));
                      }
                    }}
                  />
                  <Form.Input.Feedback
                    renderAssistiveText={() =>
                      inputNameValidity === "invalid" ? (
                        <>
                          Name must be between 5-19 characters.
                          <br />
                          Name can only contain letters.
                        </>
                      ) : null
                    }
                  />
                </Form.Group>
                <Form.Group controlId="team_description_create">
                  <Form.Input
                    rows={3}
                    columns={50}
                    maxLength={150}
                    placeholder="Please provide a brief description of this Team"
                    value={createTeam?.description}
                    style={{ resize: "none" }}
                    status={inputDescriptionValidity}
                    onBlur={() => setWasDescriptionTouched(true)}
                    onChange={(e: any) => {
                      if (e.target instanceof HTMLTextAreaElement) {
                        const value = e.target.value;
                        setCreateTeam((state) => ({
                          ...state,
                          description: value,
                        }));
                      }
                    }}
                  />

                  <Form.Input.Feedback
                    renderAssistiveText={() =>
                      inputDescriptionValidity === "invalid" ? (
                        <>Description cannot be blank.</>
                      ) : null
                    }
                  />
                </Form.Group>
                <p>
                  <small>{`${
                    150 -
                    (createTeam && createTeam.description
                      ? createTeam.description.length
                      : 0)
                  } chars left`}</small>
                </p>
                <div className="form-group">
                  {/* BUG:(Chris) Before refactor, all selects seem to highlight all options. Need to find out why! */}
                  <Select
                    options={generateAccountDropdownItems(accounts)}
                    placeholder={"Select Account..."}
                    isClearable={false}
                    onChange={onSelectAccountFromDropdown}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <input
                type="submit"
                value="Create"
                className={[
                  genStyles["alenza__btn-create__mini"],
                  "float-right",
                ].join(" ")}
                disabled={!validTeamName(createTeam?.name)}
                onClick={(e: any) => {
                  log.info("Create Team Button Clicked!");
                  setIsCreatingTeam(true);
                  onHandleCreateTeamClick(e);
                }}
              />
            </Modal.Footer>
          </form>
        </Modal>
      </>
    );
  };

  const teamSettingsModal = () => {
    return (
      <>
        <Modal
          aria-labelledby="contained-modal-title-vcenter"
          centered
          show={showSettingsModal}
          onHide={() => {
            setShowSettingsModal(false);
            setModifyTeam({ name: undefined, description: undefined });
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              Team Settings
              <FontAwesomeIcon icon={["fal", "cog"]} />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              {selectedTeam ? (
                <Link
                  style={{ marginRight: "10px" }}
                  to={r.ACCOUNT_TEAM(
                    selectedTeam.account_name.toLowerCase(),
                    selectedTeam.name.toLowerCase()
                  )}
                >
                  <button
                    type="submit"
                    className={genStyles["alenza__btn-primary__mini"]}
                  >
                    Go to {selectedTeam.name}
                    <FontAwesomeIcon icon={["fal", "long-arrow-right"]} />
                  </button>
                </Link>
              ) : null}
            </div>
          </Modal.Body>
          {userIsAdmin || userIsAccountOwner ? (
            <>
              <form name="modify-form">
                <Modal.Body>
                  <div>
                    <h5>Rename</h5>

                    <Form.Group controlId="team_name_edit">
                      <Form.Input
                        type="text"
                        placeholder="Team Name"
                        style={{ marginBottom: "25px" }}
                        value={modifyTeam?.name || ""}
                        status={getValidity()}
                        onChange={(e: any) => {
                          if (e.target instanceof HTMLInputElement) {
                            const value = e.target.value;
                            setModifyTeam((state) => ({
                              ...state,
                              name: value,
                            }));
                          }
                        }}
                      />
                    </Form.Group>
                    <h5>Edit Description</h5>

                    <Form.Group controlId="team_description_edit">
                      <Form.Input
                        rows={3}
                        columns={50}
                        maxLength={150}
                        placeholder="Please provide a brief description of this Team"
                        value={modifyTeam?.description}
                        style={{ resize: "none" }}
                        onChange={(e: any) => {
                          if (e.target instanceof HTMLTextAreaElement) {
                            const value = e.target.value;
                            setModifyTeam((state) => ({
                              ...state,
                              description: value,
                            }));
                          }
                        }}
                      />
                    </Form.Group>
                    <p>{`${
                      150 -
                      (modifyTeam && modifyTeam.description
                        ? modifyTeam.description.length
                        : 0)
                    } chars left`}</p>
                    <input
                      type="submit"
                      value="Save"
                      className={[
                        genStyles["alenza__btn-create__mini"],
                        "float-right",
                      ].join(" ")}
                      disabled={
                        !validTeamName(modifyTeam?.name) ||
                        modifyTeam?.name === selectedTeam?.name
                      }
                      onClick={(e: any) => {
                        setIsModifyingTeam(true);
                        onHandleModifyTeamClick(e);
                      }}
                    />
                  </div>
                </Modal.Body>
              </form>
              <hr />
              <form name="delete-form" style={{ marginBottom: "15px" }}>
                <Modal.Body>
                  <h5>Delete</h5>
                  <p>Please confirm the name of the Team you want to delete.</p>
                  <div>
                    <Form.Group controlId="team_name_delete">
                      <Form.Input
                        type="text"
                        placeholder="Confirm Team Name"
                        style={{ marginBottom: "25px" }}
                        value={deleteTeamConfirm || ""}
                        status={confirmTeamDelete() ? "valid" : "invalid"}
                        onChange={(e: any) => {
                          if (e.target instanceof HTMLInputElement) {
                            const value = e.target.value;
                            setDeleteTeamConfirm(value);
                          }
                        }}
                      />
                    </Form.Group>
                  </div>
                  <p>Are you sure you want to delete this Team?</p>
                  <p className="font-bold">
                    This action is permanent and irreversible.
                  </p>
                  <p>
                    All Analyses belonging to this Team will also be deleted.
                  </p>
                  <input
                    type="submit"
                    value="Delete"
                    className={[
                      genStyles["alenza__btn-delete__mini"],
                      "float-right",
                    ].join(" ")}
                    disabled={!confirmTeamDelete()}
                    onClick={(e: any) => {
                      setIsDeletingTeam(true);
                      onHandleDeleteTeamClick(e);
                    }}
                  />
                </Modal.Body>
              </form>
            </>
          ) : null}
        </Modal>
      </>
    );
  };

  return (
    <>
      {teamsCard()}
      {createTeamModal()}
      {teamSettingsModal()}
    </>
  );
};

export default TeamsCard;
